import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Button,
  Typography,
  Badge,
  Tooltip,
  message,
  Progress,
  Avatar,
} from "antd";
import {
  GiftOutlined,
  FireOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  StarFilled,
  TrophyOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  getDailyRewardsStatus,
  claimDailyLogin,
  claimDailyUsage,
  checkUsageActivity,
} from "../../../api/gamification/achievement";
import { getAdminAnalyticsAction } from "@redux/actions/adminActions";

const { Text } = Typography;

const DailyRewardsCard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [dailyStatus, setDailyStatus] = useState(null);
  const [claiming, setClaiming] = useState({ login: false, usage: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDailyStatus();
    triggerUsageCheck();
  }, []);

  const fetchDailyStatus = async () => {
    try {
      const response = await getDailyRewardsStatus();
      setDailyStatus(response.data);
    } catch (error) {
      console.error("Error fetching daily status:", error);
    } finally {
      setLoading(false);
    }
  };

  const triggerUsageCheck = async () => {
    try {
      await checkUsageActivity();
    } catch (error) {
      console.debug("Usage check trigger failed (non-critical):", error);
    }
  };

  const handleClaimDailyLogin = async () => {
    setClaiming({ ...claiming, login: true });
    try {
      const response = await claimDailyLogin();

      if (response.data.milestoneReward) {
        message.success(
          `Daily login reward claimed! 🎉 Bonus: ${response.data.milestoneReward.name} (+${response.data.milestoneReward.credits} credits)`
        );
      } else {
        message.success(
          `Daily login reward claimed! +${response.data.creditsAwarded} credits 🎉`
        );
      }

      await fetchDailyStatus();
      // Refresh admin analytics to update the plan card
      dispatch(getAdminAnalyticsAction(() => {}));
    } catch (error) {
      if (error.response?.data?.alreadyClaimed) {
        message.info("Daily login reward already claimed today!");
      } else {
        message.error("Failed to claim daily login reward");
      }
    } finally {
      setClaiming({ ...claiming, login: false });
    }
  };

  const handleClaimDailyUsage = async () => {
    setClaiming({ ...claiming, usage: true });
    try {
      const response = await claimDailyUsage();
      message.success(
        `Daily usage reward claimed! +${response.data.creditsAwarded} credits 🎉`
      );

      await fetchDailyStatus();
      // Refresh admin analytics to update the plan card
      dispatch(getAdminAnalyticsAction(() => {}));
    } catch (error) {
      if (error.response?.data?.alreadyClaimed) {
        message.info("Daily usage reward already claimed today!");
      } else {
        message.error("Failed to claim daily usage reward");
      }
    } finally {
      setClaiming({ ...claiming, usage: false });
    }
  };

  // Get streak level and next milestone
  const getStreakInfo = (streak) => {
    const milestones = [3, 7, 14, 30];
    let currentLevel = 0;
    let nextMilestone = milestones[0];

    for (let i = 0; i < milestones.length; i++) {
      if (streak >= milestones[i]) {
        currentLevel = i + 1;
        nextMilestone = milestones[i + 1] || milestones[milestones.length - 1];
      } else {
        nextMilestone = milestones[i];
        break;
      }
    }

    return { currentLevel, nextMilestone };
  };

  const getStreakColor = (streak) => {
    if (streak >= 30) return "#ff6b6b"; // Red for champion
    if (streak >= 14) return "#4ecdc4"; // Teal for expert
    if (streak >= 7) return "#45b7d1"; // Blue for advanced
    if (streak >= 3) return "#96ceb4"; // Green for beginner
    return "#feca57"; // Yellow for starter
  };

  const getStreakTitle = (streak) => {
    if (streak >= 30) return "Streak Champion";
    if (streak >= 14) return "Streak Expert";
    if (streak >= 7) return "Streak Master";
    if (streak >= 3) return "Streak Warrior";
    return "Getting Started";
  };

  if (loading) {
    return (
      <Card
        style={{
          background: "white",
          border: "1px solid #e8e8e8",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <Text style={{ color: "#595959", fontSize: "16px" }}>
            Loading daily rewards...
          </Text>
        </div>
      </Card>
    );
  }

  const currentStreak = dailyStatus?.dailyLogin?.streak || 0;
  const streakInfo = getStreakInfo(currentStreak);
  const streakColor = getStreakColor(currentStreak);

  return (
    <Card
      style={{
        background: "white",
        border: "1px solid #e8e8e8",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      bodyStyle={{
        padding: "0",
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                background: "linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "12px",
              }}
            >
              <span style={{ fontSize: "18px" }}>🎁</span>
            </div>
            <Text
              style={{
                color: "#262626",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Daily Rewards
            </Text>
          </div>
          <div
            style={{
              background: "linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)",
              borderRadius: "12px",
              padding: "4px 10px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span style={{ fontSize: "12px" }}>💰</span>
            <Text style={{ color: "#16a34a", fontSize: "12px", fontWeight: "600" }}>
              200/day
            </Text>
          </div>
        </div>

        {/* Streak Section */}
        {dailyStatus?.dailyLogin && (
          <div
            style={{
              background: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    background: streakColor,
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  }}
                >
                  <FireOutlined style={{ color: "white", fontSize: "18px" }} />
                </div>
                <div>
                  <Text
                    style={{
                      color: "#262626",
                      fontSize: "18px",
                      fontWeight: "700",
                      display: "block",
                    }}
                  >
                    {currentStreak} Day Streak
                  </Text>
                  <Text
                    style={{
                      color: "#8c8c8c",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    {getStreakTitle(currentStreak)}
                  </Text>
                </div>
              </div>

              <div style={{ textAlign: "right" }}>
                <Text
                  style={{
                    color: "#262626",
                    fontSize: "16px",
                    fontWeight: "700",
                    display: "block",
                  }}
                >
                  Best: {dailyStatus.dailyLogin.longestStreak}
                </Text>
                <Text
                  style={{
                    color: "#8c8c8c",
                    fontSize: "13px",
                    fontWeight: "500",
                  }}
                >
                  Next: {streakInfo.nextMilestone} days
                </Text>
              </div>
            </div>

            {/* Progress bar to next milestone */}
            <Progress
              percent={(currentStreak / streakInfo.nextMilestone) * 100}
              showInfo={false}
              strokeColor={{
                "0%": "#8B5CF6",
                "100%": "#EC4899",
              }}
              trailColor="#f0f0f0"
              strokeWidth={6}
              style={{ marginBottom: "8px" }}
            />

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text
                style={{
                  color: "#8c8c8c",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                Keep it up! 🔥
              </Text>
              <Text
                style={{
                  color: "#8c8c8c",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                {streakInfo.nextMilestone - currentStreak} days to milestone
              </Text>
            </div>
          </div>
        )}

        {/* Reward Cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {/* Daily Login Reward */}
          <div
            style={{
              background: "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)",
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    background: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
                    borderRadius: "6px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "12px",
                  }}
                >
                  <CalendarOutlined
                    style={{ color: "white", fontSize: "16px" }}
                  />
                </div>
                <div>
                  <Text
                    style={{
                      color: "#262626",
                      fontSize: "16px",
                      fontWeight: "600",
                      display: "block",
                    }}
                  >
                    Daily Login
                  </Text>
                  <Text
                    style={{
                      color: "#52c41a",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    +200 credits
                  </Text>
                </div>
              </div>

              {dailyStatus?.dailyLogin?.canClaim ? (
                <Button
                  type="primary"
                  size="small"
                  loading={claiming.login}
                  onClick={handleClaimDailyLogin}
                  style={{
                    background: "linear-gradient(135deg, #8B5CF6, #EC4899)",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "500",
                    height: "32px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                  }}
                  icon={<GiftOutlined />}
                >
                  Claim
                </Button>
              ) : (
                <div
                  style={{
                    background: "#f6ffed",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #b7eb8f",
                  }}
                >
                  <CheckCircleOutlined
                    style={{
                      color: "#52c41a",
                      fontSize: "14px",
                      marginRight: "6px",
                    }}
                  />
                  <Text
                    style={{
                      color: "#52c41a",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    Claimed
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Daily Usage Reward */}
          <div
            style={{
              background: "linear-gradient(135deg, #fdf2f8 0%, #faf5ff 100%)",
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    background: "linear-gradient(135deg, #EC4899, #DB2777)",
                    borderRadius: "6px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "12px",
                  }}
                >
                  <ThunderboltOutlined
                    style={{ color: "white", fontSize: "16px" }}
                  />
                </div>
                <div>
                  <Text
                    style={{
                      color: "#262626",
                      fontSize: "16px",
                      fontWeight: "600",
                      display: "block",
                    }}
                  >
                    Daily Usage
                  </Text>
                  <Text
                    style={{
                      color: "#52c41a",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    +200 credits
                  </Text>
                </div>
              </div>

              {dailyStatus?.dailyUsage?.canClaim ? (
                <Button
                  type="primary"
                  size="small"
                  loading={claiming.usage}
                  onClick={handleClaimDailyUsage}
                  style={{
                    background: "linear-gradient(135deg, #EC4899, #DB2777)",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "500",
                    height: "32px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                  }}
                  icon={<GiftOutlined />}
                >
                  Claim
                </Button>
              ) : (
                <div
                  style={{
                    background: "#fdf2f8",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #fbcfe8",
                  }}
                >
                  <ThunderboltOutlined
                    style={{
                      color: "#EC4899",
                      fontSize: "14px",
                      marginRight: "6px",
                    }}
                  />
                  <Text
                    style={{
                      color: "#EC4899",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    Auto Credited
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom encouragement message */}
        <div
          style={{
            textAlign: "center",
            marginTop: "20px",
            padding: "12px",
            background: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)",
            borderRadius: "12px",
          }}
        >
          <Text
            style={{
              fontSize: "13px",
              color: "#595959",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "500",
            }}
          >
            <StarFilled style={{ marginRight: "6px", color: "#faad14" }} />
            Login daily and use credits to boost your balance!
            <StarFilled style={{ marginLeft: "6px", color: "#faad14" }} />
          </Text>
        </div>
      </div>
    </Card>
  );
};

export default DailyRewardsCard;
